import { Navigate } from "@tanstack/react-router";
import { CalendarDays, ClipboardList, UserRound, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AanmeldShell } from "@/components/AanmeldShell";
import { Navigatiekaart } from "@/components/Navigatiekaart";
import { SkeletonLine } from "@/components/ui/skeleton";
import { GeenBevoegdheidKaart } from "@/features/beschikbaarheid/GeenBevoegdheidKaart";
import { useOndersteunerPeriodes } from "@/features/beschikbaarheid/useBeschikbaarheid";
import { GeenKorpsKaart } from "@/features/inschrijving/GeenKorpsKaart";
import { InschrijfCountdown } from "@/features/inschrijving/InschrijfCountdown";
import { useKorpsPeriodes } from "@/features/inschrijving/useInschrijving";
import { usePlanning } from "@/features/planning/usePlanning";
import { huidigePeriode, vensterStatus } from "@/features/inschrijving/venster";
import { bestemmingen, type Bestemming } from "@/features/session/toegang";
import { useMe } from "@/features/session/useMe";
import { useSeizoenen } from "@/features/session/useSeizoenen";

/**
 * Drawn only where there is a choice: most people hold one half of this site, and for them a
 * menu is a click that can only go one way. `bestemmingen` decides, and the shell reads the same
 * thing for its way back, so the two cannot disagree about whether `/` exists.
 */
export function OverzichtPage() {
  const { t } = useTranslation(["overzicht"]);
  const { data: me } = useMe();
  const { data: seizoenen } = useSeizoenen();

  const seizoen = seizoenen?.[0];
  const { data: planning } = usePlanning(seizoen?.id);

  // Before the shell rather than inside it, so the heading of a page nobody is staying on is
  // never painted. While the session is still loading `me` is undefined and the shell below
  // draws its skeleton, which is the same frame this would have to show anyway.
  //
  // Who goes where is `bestemmingen`' to decide, and deliberately not this screen's alone: the
  // back link on every screen under this one is the same rule read backwards.
  const voordeur = me ? bestemmingen(me, Boolean(planning)) : null;

  if (voordeur?.doorsturen) {
    return <Navigate to={voordeur.doorsturen} replace />;
  }

  return (
    <AanmeldShell
      voordeur
      titel={t("overzicht:titel")}
      uitleg={
        voordeur?.geen
          ? t("overzicht:geenToegang")
          : seizoen
            ? t("overzicht:uitleg", { naam: seizoen.name })
            : t("overzicht:geenSeizoen")
      }
    >
      {(session) => {
        const heeft = bestemmingen(session, Boolean(planning));

        if (heeft.geen) {
          /* Both refusals: only they know which half they came for, and one alone would
            read as the whole answer. */
          return (
            <div className="space-y-6">
              <GeenKorpsKaart />
              <GeenBevoegdheidKaart />
              {/* Reading the planning needs neither half, so it is the one thing they can do. */}
              {planning && <PlanningKaart />}
              <AccountKaart />
            </div>
          );
        }

        return (
          <div className="space-y-4">
            {heeft.lijst.includes("ploegen") && (
              <InschrijfCountdown seizoenId={seizoen?.id} />
            )}
            <div className="grid gap-4 md:grid-cols-2">
              {heeft.lijst.map((bestemming) => (
                <Kaart
                  key={bestemming}
                  bestemming={bestemming}
                  seizoenId={seizoen?.id}
                />
              ))}
              <AccountKaart />
            </div>
          </div>
        );
      }}
    </AanmeldShell>
  );
}

/** One card, for one destination this session holds. */
function Kaart({
  bestemming,
  seizoenId,
}: {
  bestemming: Bestemming;
  seizoenId: string | undefined;
}) {
  const { t } = useTranslation(["overzicht", "ploegen", "beschikbaarheid"]);

  switch (bestemming) {
    case "ploegen":
      return (
        <Navigatiekaart
          to="/ploegen"
          icon={ClipboardList}
          titel={t("ploegen:page.title")}
          omschrijving={t("overzicht:kaarten.ploegen")}
          regel={<KorpsVenster seizoenId={seizoenId} />}
        />
      );

    case "beschikbaarheid":
      return (
        <Navigatiekaart
          to="/beschikbaarheid"
          icon={Users}
          titel={t("beschikbaarheid:title")}
          omschrijving={t("overzicht:kaarten.beschikbaarheid")}
          regel={<OndersteunerVenster seizoenId={seizoenId} />}
        />
      );

    case "planning":
      return <PlanningKaart />;
  }
}

/**
 * Not a bestemming: it needs neither korps nor bevoegdheid, so everybody signed in sees it -
 * including somebody holding neither half, who may still want a wachtwoord.
 */
function AccountKaart() {
  const { t } = useTranslation(["account"]);

  return (
    <Navigatiekaart
      to="/account"
      icon={UserRound}
      titel={t("account:titel")}
      omschrijving={t("account:kaart")}
    />
  );
}

/** Appears once there is a planning to read: a card leading to "nog niet gepubliceerd"
 * answers nothing. */
function PlanningKaart() {
  const { t } = useTranslation(["overzicht", "planning"]);

  return (
    <Navigatiekaart
      to="/planning"
      icon={CalendarDays}
      titel={t("planning:title")}
      omschrijving={t("overzicht:kaarten.planning")}
    />
  );
}

/**
 * Whether there is still time is what decides whether somebody clicks. A count of ploegen would
 * need an inschrijving per korps to say what the screen behind the card says anyway.
 */
function KorpsVenster({ seizoenId }: { seizoenId: string | undefined }) {
  const { data: periodes, isPending } = useKorpsPeriodes(seizoenId);

  // A bar rather than the "geen periode" sentence: that sentence is what an answered query
  // says, and showing it while the query is still out would be a wrong answer, not a wait.
  if (seizoenId && isPending) {
    return <SkeletonLine className="w-40" />;
  }

  return vensterStatus(huidigePeriode(periodes)).label;
}

function OndersteunerVenster({ seizoenId }: { seizoenId: string | undefined }) {
  const { data: periodes, isPending } = useOndersteunerPeriodes(seizoenId);

  if (seizoenId && isPending) {
    return <SkeletonLine className="w-40" />;
  }

  return vensterStatus(huidigePeriode(periodes)).label;
}
